import { makeCustomerProduct } from "./customer-product.model";
import { BITCOIN_DECIMAL_PLACES_FORMAT, format } from "@shared/utils/number.utils";


import moment from 'moment';
import numeral from 'numeral';

export function makeBitcoinTransaction(payload) {
  return BitcoinTransaction.fromPayload(payload);
}

export class BitcoinSubtransaction {
  constructor() {
    this._amount = null;
    this._btc = null;
    this._customerProduct = null;
  }

  static fromPayload(payload, bitcoinPayload) {
    let subtransaction = new BitcoinSubtransaction();

    subtransaction.amount = numeral(payload.amount);
    subtransaction.customerProduct = makeCustomerProduct(payload.customer_product);

    let rate = numeral(bitcoinPayload.rate);
    subtransaction.btc = numeral(subtransaction.amount.value() / rate.value()).format(BITCOIN_DECIMAL_PLACES_FORMAT);

    return subtransaction;
  }

  set amount(amount) {
    this._amount = amount;
  }

  get amount() {
    return this._amount;
  }

  set btc(btc) {
    btc = numeral(btc);
    this._btc = btc;
  }

  get btc() {
    return this._btc;
  }

  set customerProduct(customerProduct) {
    this._customerProduct = customerProduct;
  }

  get customerProduct() {
    return this._customerProduct;
  }
}

export class BitcoinTransaction {
  constructor() {
    this._id = null;
    this._timeRemaining = null;
    this._receivingAddress = null;
    this._senderAddress = null;
    this._confirmationCount = null;
    this._isDeclined = false;
    this._isRateExpired = false;
    this._lockDownRateTimeRemaining = null;
    this._bitcoinSubtransactions = [];
    this._adjustedRate = null;
  }

  static fromPayload(payload) {
    const bitcoinTransaction = new BitcoinTransaction();

    bitcoinTransaction.id = payload.id;
    bitcoinTransaction.timeRemaining = payload.time_remaining;
    bitcoinTransaction.adjustedRate = payload.bitcoin.rate;
    bitcoinTransaction.receivingAddress = payload.bitcoin.receiver_unique_address;

    if (payload.bitcoin.hasOwnProperty('transaction')) {
      bitcoinTransaction.senderAddress = payload.bitcoin.transaction.sender_address;
    }

    if (payload.bitcoin.hasOwnProperty('confirmation_count')) {
      bitcoinTransaction.confirmationCount = payload.bitcoin.confirmation_count;
    }

    bitcoinTransaction.isRateExpired = payload.bitcoin.rate_expired;
    bitcoinTransaction.lockDownRateTimeRemaining = payload.lock_down_rate_time_remaining;
    bitcoinTransaction.isDeclined = payload.isDeclined;

    bitcoinTransaction.bitcoinSubtransactions = payload.subtransactions.map((subtransactionPayload) => {
      return BitcoinSubtransaction.fromPayload(subtransactionPayload, payload.bitcoin);
    });

    return bitcoinTransaction;
  }

  set id(id) {
    this._id = id;
  }

  get id() {
    return this._id;
  }

  set timeRemaining(timeRemaining) {  
    const parts = timeRemaining.split(':');
    this._timeRemaining = moment({
        hour: parts[0],
        minute: parts[1],
        second: parts[2],
    });
  }

  get timeRemaining() {
    return this._timeRemaining;
  }

  get isTimeRemainingExpired() {
    return this.timeRemaining.format('HH:mm:ss') === '00:00:00';
  }

  get receivingAddress() {
    return this._receivingAddress;
  }

  set receivingAddress(receivingAddress) {
    this._receivingAddress = receivingAddress;
  }

  get senderAddress() {
    return this._senderAddress;
  }

  set senderAddress(senderAddress) {
    this._senderAddress = senderAddress;
  }

  set confirmationCount(confirmationCount) {
    this._confirmationCount = confirmationCount;
  }

  get confirmationCount() {
    return this._confirmationCount;
  }

  set bitcoinSubtransactions(bitcoinSubtransactions) {
    this._bitcoinSubtransactions = bitcoinSubtransactions;
  }

  get bitcoinSubtransactions() {
    return this._bitcoinSubtransactions;
  }

  get hasConfirmation() {
    return this.confirmationCount > 0;
  }

  get isRequested() {
    if (this.isDeclined) {
      return false;
    }

    return this.confirmationCount === null;
  }

  get isPending() {
    if (this.isDeclined) {
      return false;
    }

    return this.confirmationCount !== null && this.confirmationCount < 3;
  }

  get isConfirmed() {
    return this.confirmationCount >= 3;
  }

  set isDeclined(isDeclined) {
    this._isDeclined = isDeclined;
  }

  get isDeclined() {
    return this._isDeclined;
  }
  
  set isRateExpired(isRateExpired) {
    this._isRateExpired = isRateExpired;
  }

  get isRateExpired() {
    return this._isRateExpired;
  }

  set lockDownRateTimeRemaining(timeRemaining) {  
    const parts = timeRemaining.split(':');
    this._lockDownRateTimeRemaining = moment({
        hour: parts[0],
        minute: parts[1],
        second: parts[2],
    });
  }

  get lockDownRateTimeRemaining() {
    return this._lockDownRateTimeRemaining;
  }

  get isLockDownRateTimeExpired() {
    return this._lockDownRateTimeRemaining.format('HH:mm:ss') === '00:00:00';
  }

  set adjustedRate(adjustedRate) {
    this._adjustedRate = numeral(adjustedRate);
  }

  get adjustedRate() {
    return this._adjustedRate;
  }

  get formattedAdjustedRate() {
    return BitcoinTransaction.formatDecimal(this.adjustedRate);
  }

  static formatDecimal(numeral) {
    return format(numeral, BITCOIN_DECIMAL_PLACES_FORMAT);
  }
}


// WEBPACK FOOTER //
// ./client/app/api/models/bitcoin-transaction.model.js